import React from 'react';
import { Text } from '@vibe/core';
import type {
  UtilizationReportResponse,
  UtilizationReportGroup,
  UtilizationReportRow,
  UtilizationCell,
  ReportColumn,
  ReportScope,
} from '../../api/report.api';

// ─── Compat: handle old data shape (dateColumns / dailyCells) from store ────

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Normalise a utilization cell — old shape has `date` instead of `startDate` */
function normCell(c: any): UtilizationCell {
  if (c.startDate) return c;
  return { ...c, startDate: c.date, endDate: c.date };
}

function getUtilCells(obj: any): UtilizationCell[] {
  const raw: any[] = obj.cells ?? obj.dailyCells ?? [];
  return raw.map(normCell);
}

function getUtilColumns(data: any): ReportColumn[] {
  if (data.columns) return data.columns;
  // Old shape: dateColumns with { date, dayOfMonth, monthLabel, isWeekend }
  return (data.dateColumns ?? []).map((c: any) => ({
    key: c.date,
    label: String(c.dayOfMonth),
    startDate: c.date,
    endDate: c.date,
    isWeekend: c.isWeekend,
  }));
}

function getUtilScope(data: any): ReportScope {
  return data.scope ?? 'day';
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCellClass(cell: UtilizationCell, scope: ReportScope): string {
  if (scope === 'day' && (cell.isWeekend || cell.isHoliday || cell.isLeave)) {
    return 'rp-report-table__cell rp-report-table__cell--off';
  }
  if (cell.scheduledHours === 0) {
    return 'rp-report-table__cell rp-report-table__cell--zero';
  }
  if (cell.utilizationRate > 120) {
    return 'rp-report-table__cell rp-report-table__cell--overloaded';
  }
  if (cell.utilizationRate > 100) {
    return 'rp-report-table__cell rp-report-table__cell--warning';
  }
  if (cell.utilizationRate > 50) {
    return 'rp-report-table__cell rp-report-table__cell--balanced';
  }
  if (cell.utilizationRate > 0) {
    return 'rp-report-table__cell rp-report-table__cell--underloaded';
  }
  return 'rp-report-table__cell rp-report-table__cell--zero';
}

function formatCellValue(cell: UtilizationCell, scope: ReportScope): string {
  if (scope === 'day' && (cell.isWeekend || cell.isHoliday || cell.isLeave)) return '-';
  return String(cell.scheduledHours);
}

function formatRate(rate: number): string {
  if (rate === 0) return '0';
  return rate.toFixed(2);
}

// ─── Month headers (only for day scope) ─────────────────────────────────────

interface MonthSpan {
  label: string;
  span: number;
}

function buildMonthSpans(columns: ReportColumn[]): MonthSpan[] {
  const spans: MonthSpan[] = [];
  let current: MonthSpan | null = null;

  for (const col of columns) {
    // Extract month label from startDate (e.g., "February 2026")
    const d = new Date(col.startDate + 'T00:00:00');
    const monthLabel = d.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
    if (!current || current.label !== monthLabel) {
      current = { label: monthLabel, span: 1 };
      spans.push(current);
    } else {
      current.span++;
    }
  }

  return spans;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  data: UtilizationReportResponse;
}

const UtilizationReportTable: React.FC<Props> = ({ data }) => {
  const columns = getUtilColumns(data);
  const scope = getUtilScope(data);
  const { groups, period } = data;
  const monthSpans = scope === 'day' ? buildMonthSpans(columns) : [];
  const fixedColCount = 4; // Name, Total Available, Utilized, Rate

  return (
    <div className="rp-report-table__wrapper">
      {/* Period label */}
      <Text
        type={Text.types.TEXT2}
        style={{ color: '#676879', marginBottom: 8 }}
      >
        Period: {period.startDate} – {period.endDate}
      </Text>

      <div className="rp-report-table__scroll">
        <table className="rp-report-table">
          <thead>
            {/* Month header row — only for day scope */}
            {scope === 'day' && (
              <tr>
                <th
                  className="rp-report-table__header-fixed"
                  colSpan={fixedColCount}
                />
                {monthSpans.map((month, i) => (
                  <th
                    key={i}
                    className="rp-report-table__month-header"
                    colSpan={month.span}
                  >
                    {month.label}
                  </th>
                ))}
              </tr>
            )}

            {/* Column headers */}
            <tr>
              <th className="rp-report-table__header-fixed rp-report-table__col-person">
                Team / Person
              </th>
              <th className="rp-report-table__header-fixed rp-report-table__col-number">
                Total Available
                <br />
                Capacity (Hours)
              </th>
              <th className="rp-report-table__header-fixed rp-report-table__col-number">
                Utilized
                <br />
                Capacity (Hours)
              </th>
              <th className="rp-report-table__header-fixed rp-report-table__col-number">
                Utilization Rate (%)
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`rp-report-table__day-header ${
                    scope === 'day' && col.isWeekend ? 'rp-report-table__day-header--weekend' : ''
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <GroupRows
                key={group.groupId}
                group={group}
                columns={columns}
                scope={scope}
                showGroupHeader={groups.length > 1 || group.groupName !== 'All Users'}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Report description */}
      <div className="rp-report-description">
        <div className="rp-report-description__title">How to read this report</div>
        <p className="rp-report-description__text">
          Each cell shows <strong>remaining estimated hours</strong> (Original Estimate minus logged worklogs)
          for future periods. The start date is clamped to today, so only work remaining from today forward is
          counted. The{' '}<strong>Utilization Rate</strong> is calculated as{' '}
          <code>Utilized Capacity / Total Available Capacity &times; 100%</code>.
          Available capacity is derived from each person&apos;s work schedule, minus holidays and approved leaves.
        </p>
        <div className="rp-report-description__subtitle">Cell color ranges</div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch rp-report-description__swatch--off" />
          <span>Gray &mdash; non-working day (weekend, holiday, or leave)</span>
        </div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch rp-report-description__swatch--underloaded" />
          <span>Blue &mdash; underloaded (1&ndash;50%): person has significant spare capacity</span>
        </div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch rp-report-description__swatch--balanced" />
          <span>Green &mdash; balanced (51&ndash;100%): healthy utilization</span>
        </div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch rp-report-description__swatch--warning" />
          <span>Orange &mdash; warning (101&ndash;120%): slightly over capacity</span>
        </div>
        <div className="rp-report-description__legend">
          <span className="rp-report-description__swatch rp-report-description__swatch--overloaded" />
          <span>Red &mdash; overloaded (&gt;120%): significantly over capacity</span>
        </div>
        <div className="rp-report-description__subtitle">Key details</div>
        <ul className="rp-report-description__list">
          <li>
            Hours shown are <strong>remaining work</strong>, not raw planned hours. For total-hours items, logged worklogs
            are subtracted from the Original Estimate and the remainder is spread across future working days.
            Items with <em>Hours Per Day</em> keep their daily rate unchanged (worklogs are not subtracted).
          </li>
          <li>
            When multiple people are assigned, hours are divided equally among assignees (if per-person distribution is enabled in the board config).
          </li>
          <li>
            Available capacity respects individual <strong>work capacity schemes</strong> (custom hours per day of week) and deducts public holidays and approved leaves.
          </li>
        </ul>
      </div>
    </div>
  );
};

// ─── Group rows ──────────────────────────────────────────────────────────────

const GroupRows: React.FC<{
  group: UtilizationReportGroup;
  columns: ReportColumn[];
  scope: ReportScope;
  showGroupHeader: boolean;
}> = ({ group, columns, scope, showGroupHeader }) => {
  // Build aggregated cells for the group header row
  const groupCells = React.useMemo(() => {
    if (!showGroupHeader) return [];
    const cellMap = new Map<string, { scheduled: number; available: number }>();
    for (const row of group.rows) {
      for (const cell of getUtilCells(row)) {
        const key = cell.startDate;
        const existing = cellMap.get(key);
        if (existing) {
          existing.scheduled += cell.scheduledHours;
          existing.available += cell.availableHours;
        } else {
          cellMap.set(key, {
            scheduled: cell.scheduledHours,
            available: cell.availableHours,
          });
        }
      }
    }
    return columns.map((col) => {
      const vals = cellMap.get(col.startDate);
      return {
        startDate: col.startDate,
        scheduled: vals ? Math.round(vals.scheduled * 100) / 100 : 0,
        available: vals ? vals.available : 0,
        isWeekend: scope === 'day' && !!col.isWeekend,
      };
    });
  }, [group, columns, scope, showGroupHeader]);

  return (
    <>
      {showGroupHeader && (
        <tr className="rp-report-table__group-row">
          <td className="rp-report-table__header-fixed rp-report-table__col-person">
            <strong>{group.groupName}</strong>
          </td>
          <td className="rp-report-table__header-fixed rp-report-table__col-number">
            <strong>{group.totalAvailableHours}</strong>
          </td>
          <td className="rp-report-table__header-fixed rp-report-table__col-number">
            <strong>{group.totalScheduledHours}</strong>
          </td>
          <td className="rp-report-table__header-fixed rp-report-table__col-number">
            <strong>{formatRate(group.utilizationRate)}</strong>
          </td>
          {groupCells.map((cell) => (
            <td
              key={cell.startDate}
              className={`rp-report-table__cell ${
                cell.isWeekend ? 'rp-report-table__cell--off' : ''
              }`}
            >
              {cell.isWeekend ? '' : cell.scheduled}
            </td>
          ))}
        </tr>
      )}
      {group.rows.map((row) => (
        <React.Fragment key={row.userId}>
          <UserRow row={row} columns={columns} scope={scope} />
          {/* Board sub-rows */}
          {row.boardSubRows?.map((subRow) => (
            <SubRow
              key={subRow.boardId}
              label={subRow.boardName}
              totalScheduledHours={subRow.totalScheduledHours}
              utilizationRate={subRow.utilizationRate}
              cells={getUtilCells(subRow)}
              columns={columns}
              scope={scope}
            />
          ))}
          {/* Item sub-rows */}
          {row.itemSubRows?.map((subRow) => (
            <SubRow
              key={subRow.itemId}
              label={subRow.itemName}
              secondaryLabel={subRow.boardName}
              totalScheduledHours={subRow.totalScheduledHours}
              utilizationRate={subRow.utilizationRate}
              cells={getUtilCells(subRow)}
              columns={columns}
              scope={scope}
            />
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

// ─── User row ────────────────────────────────────────────────────────────────

const UserRow: React.FC<{
  row: UtilizationReportRow;
  columns: ReportColumn[];
  scope: ReportScope;
}> = ({ row, columns, scope }) => {
  const normalizedCells = React.useMemo(() => getUtilCells(row), [row]);
  const cellMap = React.useMemo(() => {
    const map = new Map<string, UtilizationCell>();
    for (const cell of normalizedCells) {
      map.set(cell.startDate, cell);
    }
    return map;
  }, [normalizedCells]);

  return (
    <tr className="rp-report-table__user-row">
      <td className="rp-report-table__header-fixed rp-report-table__col-person">
        {row.userName}
      </td>
      <td className="rp-report-table__header-fixed rp-report-table__col-number">
        {row.totalAvailableHours}
      </td>
      <td className="rp-report-table__header-fixed rp-report-table__col-number">
        {row.totalScheduledHours}
      </td>
      <td className="rp-report-table__header-fixed rp-report-table__col-number">
        {formatRate(row.utilizationRate)}
      </td>
      {columns.map((col) => {
        const cell = cellMap.get(col.startDate);
        if (!cell) {
          return (
            <td key={col.key} className="rp-report-table__cell rp-report-table__cell--off">
              -
            </td>
          );
        }
        return (
          <td key={col.key} className={getCellClass(cell, scope)}>
            {formatCellValue(cell, scope)}
          </td>
        );
      })}
    </tr>
  );
};

// ─── Sub-row (board or item breakdown) ───────────────────────────────────────

const SubRow: React.FC<{
  label: string;
  secondaryLabel?: string;
  totalScheduledHours: number;
  utilizationRate: number;
  cells: UtilizationCell[];
  columns: ReportColumn[];
  scope: ReportScope;
}> = ({ label, secondaryLabel, totalScheduledHours, utilizationRate, cells, columns, scope }) => {
  const cellMap = React.useMemo(() => {
    const map = new Map<string, UtilizationCell>();
    for (const cell of cells) {
      map.set(cell.startDate, cell);
    }
    return map;
  }, [cells]);

  return (
    <tr className="rp-report-table__sub-row">
      <td className="rp-report-table__header-fixed rp-report-table__col-person rp-report-table__col-person--sub">
        <span className="rp-report-table__sub-row-label">{label}</span>
        {secondaryLabel && (
          <span className="rp-report-table__sub-row-secondary">{secondaryLabel}</span>
        )}
      </td>
      <td className="rp-report-table__header-fixed rp-report-table__col-number rp-report-table__col-number--sub">
        {/* No available hours for sub-rows */}
      </td>
      <td className="rp-report-table__header-fixed rp-report-table__col-number rp-report-table__col-number--sub">
        {totalScheduledHours}
      </td>
      <td className="rp-report-table__header-fixed rp-report-table__col-number rp-report-table__col-number--sub">
        {formatRate(utilizationRate)}
      </td>
      {columns.map((col) => {
        const cell = cellMap.get(col.startDate);
        if (!cell) {
          return (
            <td key={col.key} className="rp-report-table__cell rp-report-table__cell--off">
              -
            </td>
          );
        }
        return (
          <td key={col.key} className={getCellClass(cell, scope)}>
            {formatCellValue(cell, scope)}
          </td>
        );
      })}
    </tr>
  );
};

export default UtilizationReportTable;
